export type StatusBucket = {
  key: string
  label: string
}

export type WithStatus<T> = T & { status: string }

// Proposal Summary and Estimates hold their rows in status buckets, and one job
// can sit in several (e.g. sent and approved). Export one row per job with every
// status it holds, so totals are not double-counted and Status can be pivoted on.
// recordFrom picks whose figures the row carries when the buckets disagree (Proposal
// Summary prices a job per bucket); 'last' is the job's most advanced status.
export function flattenStatusBuckets<T extends { projectId: number }>(
  summary: Record<string, T[] | undefined>,
  buckets: StatusBucket[],
  recordFrom: 'first' | 'last' = 'first'
): WithStatus<T>[] {
  const rows = new Map<number, { row: T, statuses: string[] }>()

  buckets.forEach(bucket => {
    (summary[bucket.key] ?? []).forEach(row => {
      const existing = rows.get(row.projectId)
      if (existing) {
        if (!existing.statuses.includes(bucket.label)) {
          existing.statuses.push(bucket.label)
        }
        if (recordFrom === 'last') {
          existing.row = row
        }
      } else {
        rows.set(row.projectId, { row, statuses: [bucket.label] })
      }
    })
  })

  return Array.from(rows.values()).map(({ row, statuses }) => ({ ...row, status: statuses.join(', ') }))
}
